/**
 * @vuelto-surface
 * mobile: n/a
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { Ionicons } from '@expo/vector-icons';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { formatLocalDate } from '@shared/vueltoDateTime';
import type { BrandRecord } from '../../domain/catalogBrandTypes';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors'
import { typography } from '../../../shared/theme/typography';
import { ticketSurfaces } from '../../../shared/ticket/ticketSurfaces.styles';

type BrandMasterTableProps = {
  brands: BrandRecord[];
  onOpenDetail: (brand: BrandRecord) => void;
};

function formatWebsiteHost(website: string | null | undefined): string {
  const raw = website?.trim();
  if (!raw) return '—';
  try {
    return new URL(raw).hostname.replace(/^www\./i, '');
  } catch {
    return raw;
  }
}

export function BrandMasterTable({ brands, onOpenDetail }: BrandMasterTableProps) {
  if (brands.length === 0) return null;

  return (
    <ScrollView
      horizontal
      style={styles.tableOuter}
      contentContainerStyle={styles.tableOuterContent}
      showsHorizontalScrollIndicator={false}
    >
      <View style={styles.table}>
        <View style={[styles.row, styles.headerRow]}>
          <Text style={[styles.headerCell, styles.nameCell]}>Marca</Text>
          <Text style={[styles.headerCell, styles.codeCell]}>Código</Text>
          <Text style={[styles.headerCell, styles.markupCell]}>Margen</Text>
          <Text style={[styles.headerCell, styles.websiteCell]}>Sitio web</Text>
          <Text style={[styles.headerCell, styles.dateCell]}>Creada</Text>
          <Text style={[styles.headerCell, styles.statusCell]}>Estado</Text>
          <View style={styles.chevronCell} />
        </View>

        {brands.map((brand) => {
          const codeText = brand.code?.trim() ? brand.code.trim() : '—';
          const markupText =
            brand.defaultMarkupPercent != null ? `${brand.defaultMarkupPercent}%` : '—';
          const websiteText = formatWebsiteHost(brand.website);
          const dateText = brand.createdAt ? formatLocalDate(brand.createdAt) : '—';

          return (
            <TouchableOpacity
              key={brand.id}
              style={[styles.row, !brand.isActive && styles.rowInactive]}
              onPress={() => onOpenDetail(brand)}
              accessibilityRole="button"
              accessibilityLabel={`Detalle de ${brand.name}, ${brand.isActive ? 'activa' : 'inactiva'}`}
            >
              <View style={styles.nameCell}>
                <Text style={styles.itemName} numberOfLines={1}>
                  {brand.name}
                </Text>
                {brand.website?.trim() ? (
                  <Text style={styles.itemMeta} numberOfLines={1}>
                    {brand.website.trim()}
                  </Text>
                ) : null}
              </View>
              <Text style={[styles.codeCell, styles.codeText]} numberOfLines={1}>
                {codeText}
              </Text>
              <Text style={[styles.markupCell, styles.markupText]} numberOfLines={1}>
                {markupText}
              </Text>
              <Text style={[styles.websiteCell, styles.websiteText]} numberOfLines={1}>
                {websiteText}
              </Text>
              <Text style={[styles.dateCell, styles.dateText]} numberOfLines={1}>
                {dateText}
              </Text>
              <View style={styles.statusCell}>
                <StatusPill
                  label={brand.isActive ? 'Activa' : 'Inactiva'}
                  tone={brand.isActive ? 'success' : 'danger'}
                  compact
                />
              </View>
              <View style={styles.chevronCell}>
                <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  tableOuter: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    marginBottom: spacing.sm,
  },
  tableOuterContent: {
    minWidth: '100%',
  },
  table: {
    ...ticketSurfaces.paperSheet,
    width: '100%',
    minWidth: 700,
  },
  row: {
    minHeight: 60,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 13,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  rowInactive: {
    opacity: 0.55,
  },
  headerRow: {
    minHeight: 40,
    backgroundColor: ticketColors.paper2,
  },
  headerCell: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontSize: 10,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  nameCell: {
    flexGrow: 3,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 200,
    gap: 2,
  },
  codeCell: {
    flexGrow: 0,
    flexShrink: 1,
    width: 120,
    minWidth: 88,
  },
  markupCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 72,
    textAlign: 'right',
  },
  websiteCell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 120,
    maxWidth: 200,
  },
  dateCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 108,
  },
  statusCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 96,
  },
  chevronCell: {
    flexGrow: 0,
    flexShrink: 0,
    width: 20,
    alignItems: 'center',
  },
  itemName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
  },
  itemMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
  codeText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  markupText: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  websiteText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  dateText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
});
